import React from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { formatRoundFixed } from '@benzinga/utils/numberUtils';
import { apostrophyName } from '@benzinga/utils/stringUtils';
import { getLastDividendInfo, getNextDividendInfo, type DividendSummary } from '../../../utils/quote/summaries';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import type { Dividend } from '@benzinga/calendar-manager';
import { formatTickerExchange } from '../../../../utils/exchange';
import { formatDate, isPastDate } from './utils';
import FAQs from '../../../components/FAQs';

interface DividendFAQProps {
  dividendSummary: DividendSummary;
  quote: DelayedQuote;
}

const DividendsFAQ: React.FC<DividendFAQProps> = ({ dividendSummary, quote }) => {
  const { t, i18n } = useTranslation(['quote', 'common']);
  const language = i18n.language;

  const FAQsDataSet = [
    {
      answer: getDividendInfo(dividendSummary?.dividends, quote, t, language),
      question: t('Quote.FAQ.Questions.Dividends.when-does-pay-its-next-dividend', {
        quoteName: quote?.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getLatestDividendPaidOutInfo(dividendSummary?.dividends, quote, t, language),
      question: t('Quote.FAQ.Questions.Dividends.what-date-did-i-need-to-own', {
        quoteName: quote?.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getHowMuchPerShareInfo(dividendSummary?.dividends, quote, t, language),
      question: t('Quote.FAQ.Questions.Dividends.how-much-per-share-is-the-next', {
        quoteName: quote?.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getDividendYieldInfo(dividendSummary?.dividends, quote, t, language),
      question: t('Quote.FAQ.Questions.Dividends.what-is-the-dividend-yield-for', {
        exchange: formatTickerExchange(quote.bzExchange, quote.otcTier),
        quoteName: quote?.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getDividendAnnouncementDate(dividendSummary?.dividends, quote, t, language),
      question: t('Quote.FAQ.Questions.Dividends.what-was-the-announcement-date', {
        quoteName: quote?.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getPeersDividendComparison(dividendSummary?.peersAverageDividend ?? 0, quote, t),
      question: t('Quote.FAQ.Questions.Dividends.why-is-dividend-considered', {
        quoteName: quote?.name,
        quoteSymbol: quote.symbol,
        status:
          (quote?.dividend ?? 0) > (dividendSummary?.peersAverageDividend ?? 0)
            ? t('Quote.Market.high')
            : t('Quote.Market.low'),
      }),
    },
    {
      answer: getDividendGoodEval(dividendSummary, quote, t),
      question: t('Quote.FAQ.Questions.Dividends.is-a-good-dividend', {
        quoteName: quote?.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getDividendSustainableEval(dividendSummary?.sustainabilityData, quote, t),
      question: t('Quote.FAQ.Questions.Dividends.is-the-dividend-sustainable', {
        quoteName: quote?.name,
        quoteSymbol: quote.symbol,
      }),
    },
  ];

  const title = `${quote?.name}`;

  return <FAQs FAQsDataSet={FAQsDataSet} title={title} />;
};

export default DividendsFAQ;

const getLinkToDividendsTab = (symbol: string): React.ReactElement => {
  return <a href={`https://www.benzinga.com/quote/${symbol}/dividends`}>last dividend paid</a>;
};

const parseIsoDate = (dateString: string | undefined, language: string): DateTime | null =>
  dateString ? DateTime.fromISO(dateString).setLocale(language) : null;

const getDividendInfo = (
  dividendData: Dividend[],
  quote: DelayedQuote,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.there-are-no-upcoming-dividends-for', { quoteName: quote?.name })}</p>;

  if (!dividendData || dividendData.length === 0) {
    return fallbackValue;
  }

  try {
    const nextDividend = getNextDividendInfo(dividendData);

    if (!nextDividend) {
      const lastDividends = getLastDividendInfo(dividendData);
      if (!lastDividends) {
        return fallbackValue;
      }
      const payableDate = parseIsoDate(lastDividends.payable_date, language);
      if (!payableDate) {
        return fallbackValue;
      }
      const formattedDividendYield = `$${Number(lastDividends.dividend).toFixed(2)}`;
      return (
        <p>
          {t('Quote.FAQ.Answers.there-are-no-upcoming-dividends-for', { quoteName: quote?.name })}
          {t('Helpers.the', { ns: 'common' })} {getLinkToDividendsTab(quote.symbol)}{' '}
          {t('Quote.FAQ.Answers.Dividends.the-out-to-investors-was', {
            date: formatDate(payableDate),
            formattedDividendYield,
          })}
        </p>
      );
    }

    const futureDate = parseIsoDate(nextDividend.ex_dividend_date, language);
    const announcedDate = parseIsoDate(nextDividend.date, language);
    if (!futureDate || !announcedDate) {
      return fallbackValue;
    }

    return (
      <p>
        {t('Quote.FAQ.Answers.Dividends.next-dividend-was-announced-on', {
          announcedDate: formatDate(announcedDate),
          futureDate: formatDate(futureDate),
          quoteName: apostrophyName(quote?.name),
        })}
      </p>
    );
  } catch (_error) {
    return fallbackValue;
  }
};

const getLatestDividendPaidOutInfo = (
  dividendData: Dividend[],
  quote: DelayedQuote,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.there-are-no-upcoming-dividends-for', { quoteName: quote?.name })}</p>;
  if (!dividendData || dividendData.length === 0) {
    return fallbackValue;
  }

  try {
    const relevantDividend = getNextDividendInfo(dividendData) ?? getLastDividendInfo(dividendData);
    if (!relevantDividend) {
      return fallbackValue;
    }
    const payableDate = parseIsoDate(relevantDividend.payable_date, language);
    if (!payableDate) {
      return fallbackValue;
    }
    const recordDate = parseIsoDate(relevantDividend.record_date, language);
    const dividend = Number(relevantDividend.dividend);
    const formattedDividend = `$${formatRoundFixed(dividend, 2)}`;

    if (isPastDate(payableDate)) {
      return (
        <p>
          {t('Quote.FAQ.Answers.there-are-no-upcoming-dividends-for', { quoteName: quote?.name })}
          {t('Helpers.the', { ns: 'common' })} {getLinkToDividendsTab(quote.symbol)}{' '}
          <a href={`https://www.benzinga.com/quote/${quote.symbol}/dividends`}>
            {' '}
            {t('Quote.FAQ.Answers.Dividends.last-dividend-payout', {})}
          </a>{' '}
          {t('Quote.FAQ.Answers.Dividends.and-was', {
            date: formatDate(payableDate),
            formattedDividend,
          })}
        </p>
      );
    } else if (recordDate) {
      return (
        <p>
          {t('Quote.FAQ.Answers.Dividends.the-next-dividend-payout-for', {
            payableDate: formatDate(payableDate),
            quoteName: quote?.name,
            quoteSymbol: quote?.symbol,
            recordDate: formatDate(recordDate),
          })}
        </p>
      );
    }

    return (
      <p>
        {t('Quote.FAQ.Answers.Dividends.the-next-dividend-for', {
          formattedDividend,
          payableDate: formatDate(payableDate),
          quoteName: quote?.name,
          quoteSymbol: quote?.symbol,
        })}
      </p>
    );
  } catch (_error) {
    return fallbackValue;
  }
};

const getHowMuchPerShareInfo = (
  dividendData: Dividend[],
  quote: DelayedQuote,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.there-are-no-upcoming-dividends-for', { quoteName: quote?.name })}</p>;

  if (!dividendData || dividendData.length === 0) {
    return fallbackValue;
  }

  try {
    const relevantDividend = getNextDividendInfo(dividendData) ?? getLastDividendInfo(dividendData);
    if (!relevantDividend) {
      return fallbackValue;
    }
    const payableDate = parseIsoDate(relevantDividend.payable_date, language);
    if (!payableDate) {
      return fallbackValue;
    }
    const dividend = Number(relevantDividend.dividend);
    const formattedDividend = `$${formatRoundFixed(dividend, 2)}`;

    if (isPastDate(payableDate)) {
      return (
        <p>
          {t('Quote.FAQ.Answers.there-are-no-upcoming-dividends-for', { quoteName: quote?.name })}
          {t('Helpers.the', { ns: 'common' })} {getLinkToDividendsTab(quote.symbol)}{' '}
          {t('Quote.FAQ.Answers.Dividends.the-out-to-investors-was', {
            date: formatDate(payableDate),
            formattedDividendYield: formattedDividend,
          })}
        </p>
      );
    } else {
      return (
        <p>
          {t('Quote.FAQ.Answers.Dividends.the-next-dividend-for', {
            formattedDividend,
            payableDate: formatDate(payableDate),
            quoteName: quote?.name,
            quoteSymbol: quote?.symbol,
          })}
        </p>
      );
    }
  } catch (_error) {
    return fallbackValue;
  }
};

const getDividendYieldInfo = (
  dividendData: Dividend[],
  quote: DelayedQuote,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.there-are-no-upcoming-dividends-for', { quoteName: quote?.name })}</p>;

  if (!dividendData || dividendData.length === 0) {
    return fallbackValue;
  }

  try {
    const nextDividend = getNextDividendInfo(dividendData);

    if (!nextDividend) {
      const lastDividends = getLastDividendInfo(dividendData);
      if (!lastDividends) {
        return fallbackValue;
      }
      const payableDate = parseIsoDate(lastDividends.payable_date, language);
      if (!payableDate) {
        return fallbackValue;
      }
      const formattedDividendYield = `$${Number(lastDividends.dividend).toFixed(2)}`;
      const hasDividendBeenPaid = isPastDate(payableDate)
        ? t('Quote.FAQ.Answers.Dividends.was-paid-out', {})
        : t('Quote.FAQ.Answers.Dividends.will-be-paid-out-next', {});
      return (
        <p>
          {t('Quote.FAQ.Answers.Dividends.has-no-upcoming-dividends-reported', {
            formattedDividendYield,
            hasDividendBeenPaid,
            payableDate: formatDate(payableDate),
            quoteName: quote?.name,
            quoteSymbol: quote.symbol,
          })}
        </p>
      );
    }

    const yieldValue = Number(nextDividend.dividend_yield) * 100;
    const futureFormattedYield = `${formatRoundFixed(yieldValue, 2)}%`;
    const futurePayableDate = parseIsoDate(nextDividend.payable_date, language);
    if (!futurePayableDate) {
      return fallbackValue;
    }

    return (
      <p>
        {t('Quote.FAQ.Answers.Dividends.the-most-current-yield-for', {
          futureFormattedYield,
          futurePayableDate: formatDate(futurePayableDate),
          quoteName: quote?.name,
          quoteSymbol: quote.symbol,
        })}
      </p>
    );
  } catch (_error) {
    return fallbackValue;
  }
};

const getDividendAnnouncementDate = (
  dividendData: Dividend[],
  quote: DelayedQuote,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const fallbackValue = (
    <p>
      {t('Quote.FAQ.Answers.Dividends.there-are-no-upcoming-dividends-announced-for', {
        quoteName: quote?.name,
      })}
    </p>
  );

  if (!dividendData || dividendData.length === 0) {
    return fallbackValue;
  }

  try {
    const nextDividend = getNextDividendInfo(dividendData);

    if (!nextDividend) {
      const lastDividends = getLastDividendInfo(dividendData);
      if (!lastDividends) {
        return fallbackValue;
      }
      const announcedDate = parseIsoDate(lastDividends.date, language);
      if (!announcedDate) {
        return fallbackValue;
      }
      return (
        <p>
          {t('Quote.FAQ.Answers.Dividends.the-last-dividend-was-announced-on', {
            announcedDate: formatDate(announcedDate),
            quoteName: quote?.name,
          })}
        </p>
      );
    }

    const announcedDate = parseIsoDate(nextDividend.date, language);
    if (!announcedDate) {
      return fallbackValue;
    }

    return (
      <p>
        {t('Quote.FAQ.Answers.Dividends.the-next-dividend-was-announced-on', {
          announcedDate: formatDate(announcedDate),
          quoteName: quote?.name,
          quoteSymbol: quote?.symbol,
        })}
      </p>
    );
  } catch (_error) {
    return fallbackValue;
  }
};

const getPeersDividendComparison = (
  peersAverageDividend: number,
  quote: DelayedQuote,
  t: TFunction,
): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.Dividends.not-enough-data-to-provide-evaluation')}</p>;

  if (!peersAverageDividend) {
    return fallbackValue;
  }

  try {
    return (
      <p>
        {t('Quote.FAQ.Answers.Dividends.dividend-decisions', {
          direction:
            (quote?.dividendYield ?? 0) > peersAverageDividend
              ? t('Market.Direction.higher')
              : t('Market.Direction.lower'),
          quoteName: quote?.name,
        })}
      </p>
    );
  } catch (_error) {
    return fallbackValue;
  }
};

const getConjunction = (condition1: boolean, condition2: boolean, t: TFunction) => {
  if ((condition1 && condition2) || (!condition1 && !condition2)) {
    return t('Helpers.and', { ns: 'common' });
  }
  return t('Helpers.but', { ns: 'common' });
};

const getDividendGoodEval = (dividendData: DividendSummary, quote: DelayedQuote, t: TFunction): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.Dividends.not-enough-data-to-provide-evaluation')}</p>;

  const peersDividend = dividendData?.peersAverageDividend ?? 0;
  const yearOverYearGrowth = dividendData?.yearOverYearGrowth ?? 0;
  const quoteDividend = quote?.dividend ?? 0;

  const isGood = peersDividend < quoteDividend && yearOverYearGrowth > 0;

  if (!dividendData || !peersDividend || yearOverYearGrowth === null) {
    return fallbackValue;
  }

  const good = isGood ? ` ${t('Helpers.a-good', { ns: 'common' })}` : ` ${t('Helpers.not-a-good', { ns: 'common' })}`;
  const marketDirection =
    yearOverYearGrowth === 0
      ? t('Market.Direction.stagnant')
      : yearOverYearGrowth > 0
        ? t('Market.Direction.positive')
        : t('Market.Direction.negative');

  const position = quoteDividend > peersDividend ? ` ${t('Helpers.above')}` : ` ${t('Helpers.below')}`;
  const positive = isGood ? t('Helpers.yes') : t('Helpers.no');

  try {
    return (
      <p>
        {t('Quote.FAQ.Answers.Dividends.dividend-considered', {
          conjunction: getConjunction(peersDividend < quoteDividend, yearOverYearGrowth > 0, t),
          good,
          marketDirection,
          position,
          positive,
          quoteName: quote?.name,
        })}
      </p>
    );
  } catch (_error) {
    return fallbackValue;
  }
};

const getDividendSustainableEval = (dividendData: any, quote: DelayedQuote, t: TFunction): React.ReactElement => {
  const fallbackValue = <p>{t('Quote.FAQ.Answers.Dividends.not-enough-data-to-provide-evaluation')}</p>;

  const earningsAvg = dividendData?.earningsAvgValue;
  const dividendConsistency = dividendData?.dividendConsistency;

  const isGood = earningsAvg > 0 && dividendConsistency;

  if (!dividendData || !earningsAvg) {
    return fallbackValue;
  }

  const good = isGood ? ` ${t('Helpers.a-good')}` : ` ${t('Helpers.not-a-good')}`;
  const positive = isGood ? ` ${t('Helpers.is')}` : ` ${t('Helpers.is-not')}`;

  try {
    return (
      <p>
        {t('Quote.FAQ.Answers.Dividends.dividend-considered', {
          conjunction: getConjunction(earningsAvg > 0, dividendConsistency, t),
          dividendConsistency: dividendConsistency ? '' : t('Helpers.not', { ns: 'common' }),
          earningsAvg: earningsAvg > 0 ? '' : t('Helpers.not', { ns: 'common' }),
          good,
          positive,
          quoteName: quote?.name,
        })}
      </p>
    );
  } catch (_error) {
    return fallbackValue;
  }
};
